import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import type {
  AssistantProfile,
  OrgAssistantDefaults,
  Ticket,
  TicketListResponse,
  TicketRun,
  TicketTemplate,
  ToolCatalogEntry,
  TriggerSource,
} from "../types/tickets";

const BASE = "/api/assistant";

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getValidAccessTokenFromStorage();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
  if (!res.ok) throw new Error(`${init.method || "GET"} ${path} → ${res.status}`);
  return res.status === 204 ? (undefined as T) : res.json();
}

export const ticketsApi = {
  list: () => req<TicketListResponse>("/tickets/"),
  get: (id: string) => req<Ticket>(`/tickets/${id}/`),
  create: (body: Partial<Ticket>) => req<Ticket>("/tickets/", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Ticket>) => req<Ticket>(`/tickets/${id}/`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => req<void>(`/tickets/${id}/`, { method: "DELETE" }),
  activate: (id: string) => req<Ticket>(`/tickets/${id}/activate/`, { method: "POST" }),
  pause: (id: string) => req<Ticket>(`/tickets/${id}/pause/`, { method: "POST" }),
  runNow: (id: string) => req<{ queued: boolean }>(`/tickets/${id}/run-now/`, { method: "POST" }),
  test: (id: string) => req<TicketRun>(`/tickets/${id}/test/`, { method: "POST" }),
  /** Task 4 (Tier D): resolve a paused `awaiting_approval` run. */
  approve: (runId: string) => req<TicketRun>(`/runs/${runId}/approve/`, { method: "POST" }),
  reject: (runId: string) => req<TicketRun>(`/runs/${runId}/reject/`, { method: "POST" }),
  runs: async (id: string) => {
    const data = await req<TicketRun[] | { results: TicketRun[] }>(`/tickets/${id}/runs/`);
    return (Array.isArray(data) ? data : (data.results ?? [])) as TicketRun[];
  },
  templates: () => req<TicketTemplate[]>("/ticket-templates/"),
  triggerSources: () => req<TriggerSource[]>("/trigger-sources/"),
  tools: () => req<ToolCatalogEntry[]>("/tools/"),
  getProfile: () => req<AssistantProfile>("/assistant-profile/"),
  updateProfile: (body: Partial<AssistantProfile>) =>
    req<AssistantProfile>("/assistant-profile/", { method: "PATCH", body: JSON.stringify(body) }),
  getOrgDefaults: (orgId: string) =>
    req<OrgAssistantDefaults>(`/admin/organizations/${orgId}/assistant-defaults/`),
  updateOrgDefaults: (orgId: string, body: Partial<OrgAssistantDefaults>) =>
    req<OrgAssistantDefaults>(`/admin/organizations/${orgId}/assistant-defaults/`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};
